import type { BoardProperty } from '../../@types/Board.js';
import type { ITSPayloadBoard } from '../../@types/ITSPayload.js';
import { type ReductionOutcome, SCORE_REDUCTIONS } from '../../@types/Reduction.js';
import type { SystemType } from '../../@types/SystemType.js';
import { asStringArray } from '../../utils/asStringArray.js';
import { bitmapToPositions } from '../../utils/bitwise.js';
import BoardPoolgames from './BoardPoolgames.js';

type ReductionTuple = readonly [number, ReductionOutcome[]];

export type ITSScoreBoard = ITSPayloadBoard<{
  boardData: string[][];
  boardProps: readonly BoardProperty[] | undefined;
  systemType: SystemType[];
  rSysData: ReductionOutcome[][] | undefined;
  rowPriceMulti: number;
}>;

class BoardScore extends BoardPoolgames {
  private readonly _reductions: ReductionTuple[] = [];

  get reductions(): ReductionOutcome[][] {
    if (this.systemType !== 'RSYS') {
      return [];
    }

    return this.outcomes.map((_unused, index) => {
      const [, reduction] = this._reductions.find(([eventIndex]) => eventIndex === index) ?? [];
      if (reduction) {
        return reduction;
      }

      return SCORE_REDUCTIONS;
    });
  }

  override get systemType(): SystemType {
    const isReduced = this._reductions.some((reduction) => reduction.length !== SCORE_REDUCTIONS.length);
    if (isReduced) {
      return 'RSYS';
    }

    return super.systemType;
  }

  /**
   * Sets reduction for event.
   *
   * @param eventNumber Number | Number for the event to add reduction on
   * @param reductions String/number | Binary value of reductions to have or string with value
   * @returns updated instance
   */
  addReduction(eventNumber: number, reductions: ReductionOutcome[] | number): this {
    const reductionArray: ReductionOutcome[] = [];
    if (typeof reductions === 'number') {
      const positions = bitmapToPositions(reductions);
      for (const position of positions) {
        const reduction = SCORE_REDUCTIONS[position];
        if (reduction) {
          reductionArray.push(reduction);
        }
      }
    } else {
      reductionArray.push(...reductions);
    }

    this._reductions.push([eventNumber - 1, reductionArray]);

    return this;
  }

  /**
   * If systemType is MSYS it calculates correct row count for board.
   * If reduction is set it calculates correct row count with valid rows after reduction.
   *
   * @returns Number | The given row count for board
   */
  override getRowCount(): number {
    if (this.systemType === 'MSYS') {
      return super.getRowCount();
    }

    // if (this.systemType === 'SROW') {
    return 1; // Assume if it's SROW there can only be 1 row.
    // }

    // const reductions = this.reductions;
    // const rowCounts = this.outcomes.reduce((sum, outcomeGroup, eventIndex) => {
    //   const currentReduction = reductions[eventIndex];

    //   const filterFns = currentReduction ? currentReduction.map((reduction) => scoreFilterFnMap[reduction]) : Object.values(scoreFilterFnMap);
    //   const allSROW = getCartesianProducts(...outcomeGroup);

    //   const filteredSROW = allSROW.filter((result) => filterFns.some((filterFn) => filterFn(...result)));

    //   return filteredSROW.length * (sum || 1);
    // }, 0);

    // return rowCounts;
  }

  /**
   * Used for tracking and logs.
   *
   * @param isCompact - If true, returns a more compact format.
   * @returns String | String with boardData, reduction, boardProperties and systemType
   */
  override toString(isCompact?: boolean): string {
    const logString = super.toString(isCompact);

    const logEntries = [logString];
    if (!isCompact) {
      const outcomeString = this.outcomes
        .map((board) => asStringArray(board.map((selection) => asStringArray(selection))))
        .join(',');
      const reductionString = this.reductions.map(
        (reduction, eventIndex) => `${eventIndex + 1}#${reduction.map((r) => SCORE_REDUCTIONS.indexOf(r)).join(',')}`
      );

      logEntries.unshift(`B:${outcomeString}`);
      logEntries.push(`Rd:${reductionString.toString()}`);
    }

    return logEntries.join(' ');
  }

  /**
   * Sets 10(10+) to F for external score.
   *
   * @returns Array with outcomes as a string
   */
  composeOwnRows(): ITSPayloadBoard<string> {
    const outcomes: (string | number)[][] = [];
    const { productConfig } = this;
    const maxOutComeBound = productConfig.outcomeBounds[1] || 10;
    for (const eventOutcome of this.outcomes) {
      for (const outcome of eventOutcome) {
        outcomes.push(outcome[0] === maxOutComeBound ? ['F'] : outcome);
      }
    }
    return outcomes.join('') as ITSPayloadBoard<string>;
  }

  /**
   * Loops over boards to generate correct data for all boards.
   *
   * @returns Object | Returns an object with correct data and structure for ITS-payment
   */
  compose(): ITSScoreBoard {
    const boardData = this.outcomes.reduce<string[][]>((scores, outcomeGroup) => {
      const score = outcomeGroup.reduce<string[][]>(
        (outcomes, currentOutcomes) => [...outcomes, currentOutcomes.map((outcome) => outcome.toString())],
        []
      );

      return [...scores, ...score];
    }, []);

    const rSysData = this.reductions.length > 0 ? this.reductions : undefined;

    const itsBoard = {
      boardData,
      boardProps: this.boardProperties.length > 0 ? this.boardProperties : undefined,
      systemType: [this.systemType],
      rSysData,
      // The board rowPriceMulti is employed to multiply the bets by rowPriceMulti.
      // The outcome of this equation is subsequently multiplied by the system size, resulting in the total price.
      // eg. bet.rowPriceMulti(2) * board.rowPriceMulti(3) * systemSize(4) = 2 * 3 * 4 = 24
      rowPriceMulti: 1,
    };

    type ITSBoardScore = ITSPayloadBoard<typeof itsBoard>;

    return itsBoard as ITSBoardScore;
  }
}

export default BoardScore;
